import { useMemo, useReducer } from "react";

import type { IGradeTarget } from "@/@types/campaign-grade";

import { gradeTargetKey } from "../../utils/grade-targets";

export type GradeDraftMessage =
  | { readonly kind: "none" }
  | { readonly kind: "block"; readonly text: string }
  | { readonly kind: "warn"; readonly text: string };

export interface GradeTargetDraftState {
  readonly targets: IGradeTarget[];
  readonly color: string;
  readonly size: string;
  readonly allColors: boolean;
  readonly allSizes: boolean;
  readonly message: GradeDraftMessage;
  readonly confirmingPending: boolean;
}

export type GradeTargetDraftAction =
  | { type: "SET_COLOR"; payload: string }
  | { type: "SET_SIZE"; payload: string }
  | { type: "TOGGLE_ALL_COLORS"; payload: boolean }
  | { type: "TOGGLE_ALL_SIZES"; payload: boolean }
  | { type: "COMMIT_TARGET" }
  | { type: "REMOVE_TARGET"; payload: IGradeTarget }
  | { type: "BLOCK"; payload: string }
  | { type: "WARN"; payload: string }
  | { type: "CLEAR_MESSAGE" }
  | { type: "CONFIRM_PENDING"; payload: boolean }
  | { type: "RESET"; payload: IGradeTarget[] };

export const EMPTY_GRADE_DRAFT: GradeTargetDraftState = {
  targets: [],
  color: "",
  size: "",
  allColors: false,
  allSizes: false,
  message: { kind: "none" },
  confirmingPending: false,
};

const NO_MESSAGE: GradeDraftMessage = { kind: "none" };

export const currentTargetOf = (state: GradeTargetDraftState): IGradeTarget => ({
  color: state.allColors ? null : state.color || null,
  size: state.allSizes ? null : state.size || null,
});

const HANDLERS: Record<
  GradeTargetDraftAction["type"],
  (
    state: GradeTargetDraftState,
    action: GradeTargetDraftAction,
  ) => GradeTargetDraftState
> = {
  SET_COLOR: (state, action) => ({
    ...state,
    color: (action as { payload: string }).payload,
    message: NO_MESSAGE,
  }),
  SET_SIZE: (state, action) => ({
    ...state,
    size: (action as { payload: string }).payload,
    message: NO_MESSAGE,
  }),
  TOGGLE_ALL_COLORS: (state, action) => ({
    ...state,
    allColors: (action as { payload: boolean }).payload,
    color: "",
    message: NO_MESSAGE,
  }),
  TOGGLE_ALL_SIZES: (state, action) => ({
    ...state,
    allSizes: (action as { payload: boolean }).payload,
    size: "",
    message: NO_MESSAGE,
  }),
  COMMIT_TARGET: state => ({
    ...state,
    targets: [...state.targets, currentTargetOf(state)],
    color: "",
    size: "",
    allColors: false,
    allSizes: false,
    message: NO_MESSAGE,
  }),
  REMOVE_TARGET: (state, action) => ({
    ...state,
    targets: state.targets.filter(
      item =>
        gradeTargetKey(item) !==
        gradeTargetKey((action as { payload: IGradeTarget }).payload),
    ),
    message: NO_MESSAGE,
  }),
  BLOCK: (state, action) => ({
    ...state,
    message: { kind: "block", text: (action as { payload: string }).payload },
  }),
  WARN: (state, action) => ({
    ...state,
    message: { kind: "warn", text: (action as { payload: string }).payload },
  }),
  CLEAR_MESSAGE: state => ({ ...state, message: NO_MESSAGE }),
  CONFIRM_PENDING: (state, action) => ({
    ...state,
    confirmingPending: (action as { payload: boolean }).payload,
  }),
  RESET: (state, action) => ({
    ...EMPTY_GRADE_DRAFT,
    targets: [...(action as { payload: IGradeTarget[] }).payload],
    confirmingPending: state.confirmingPending,
  }),
};

export function gradeTargetDraftReducer(
  state: GradeTargetDraftState,
  action: GradeTargetDraftAction,
): GradeTargetDraftState {
  const handler = HANDLERS[action.type];

  return handler ? handler(state, action) : state;
}

export interface GradeTargetDraft {
  state: GradeTargetDraftState;
  dispatch: React.Dispatch<GradeTargetDraftAction>;
  currentTarget: IGradeTarget;
  blockMessage: string;
  warnMessage: string;
  commitTarget: () => IGradeTarget[];
}

export function useGradeTargetDraft(
  initialTargets: IGradeTarget[],
): GradeTargetDraft {
  const [state, dispatch] = useReducer(gradeTargetDraftReducer, {
    ...EMPTY_GRADE_DRAFT,
    targets: initialTargets,
  });

  const currentTarget: IGradeTarget = useMemo(
    () => currentTargetOf(state),
    [state],
  );

  const commitTarget = (): IGradeTarget[] => {
    const next: IGradeTarget[] = [...state.targets, currentTarget];
    dispatch({ type: "COMMIT_TARGET" });

    return next;
  };

  return {
    state,
    dispatch,
    currentTarget,
    blockMessage: state.message.kind === "block" ? state.message.text : "",
    warnMessage: state.message.kind === "warn" ? state.message.text : "",
    commitTarget,
  };
}
